import {
  getComparisonTableDropdownIndices,
  numberSupportedSections,
} from '@/decodo/blocks/ProductComparisonTable/comparisonUtils/utils';
import RichTextFieldStrapi from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/decodo/components/atoms/Text';

import { cn } from '@/lib/utils';
import { ProductComparisonItemProps } from '../../ProductComparisonTable';
import {
  ProductCategoryTitles,
  ProductCategoryTitlesProps,
} from '../comparisonElements/ProductCategory';
import { ProductFeatures } from '../comparisonElements/ProductFeatureData';

interface CreateAccordionArgs {
  id: string;
  title?: string;
  categoryTitles?: ProductCategoryTitlesProps[];
  featureKey?: keyof ProductComparisonItemProps;
  isRichText?: boolean;
  selectedData: (ProductComparisonItemProps | null)[];
}
const fallbackContent = <Text className='text-base-black'>–</Text>;
const emptyContent = <span />;

export const createDropdownAccordion = ({
  id,
  title,
  categoryTitles,
  featureKey,
  isRichText = false,
  selectedData,
}: CreateAccordionArgs) => {
  if (!title || !categoryTitles) return null;

  const selectedCount = selectedData.filter(Boolean).length;
  const maxColumns = numberSupportedSections.desktop;

  const getCellContent = (i: number, rowIdx: number): React.ReactNode => {
    const selected = selectedData[i];

    if (i === selectedData.length) {
      return fallbackContent;
    }

    if (!featureKey) {
      return emptyContent;
    }

    const raw = selected?.[featureKey];
    const content = Array.isArray(raw) ? raw[rowIdx] : null;

    const position = i + 1;

    if (position <= selectedCount) {
      return content ? renderContent(content) : fallbackContent;
    }
    if (position > selectedCount) {
      return emptyContent;
    }

    return emptyContent;
  };

  const renderContent = (content: any) => {
    return isRichText ? (
      <RichTextFieldStrapi
        block={content}
        classNames={{ link: { inner: 'text-wrap whitespace-pre text-body-m' } }}
      />
    ) : (
      <ProductFeatures {...content} />
    );
  };

  const MobileContent = categoryTitles.map((titleItem, rowIdx) => {
    if (!titleItem || !featureKey) {
      console.error(`Missing titleItem or featureKey for ${titleItem}`);
      return null;
    }

    const raw1 = selectedData[0]?.[featureKey];
    const raw2 = selectedData[1]?.[featureKey];

    const content1 = Array.isArray(raw1) ? raw1[rowIdx] : null;
    const content2 = Array.isArray(raw2) ? raw2[rowIdx] : null;

    const placeholderForSecond = selectedCount < numberSupportedSections.mobile;

    return (
      <div
        key={`${id}-mobile-row-${rowIdx}`}
        className='text-body-m-medium mb-4'
      >
        <ProductCategoryTitles {...titleItem} />
        <div className='relative grid grid-cols-2 gap-4 p-2'>
          <div className='bg-base-white flex items-center border-r border-gray-200 pr-4 '>
            {content1 ? (
              renderContent(content1)
            ) : (
              <Text className=' text-base-black'>–</Text>
            )}
          </div>
          <div className='flex  items-center p-2 pl-4'>
            {placeholderForSecond ? (
              <Text className='text-base-black '>–</Text>
            ) : content2 ? (
              renderContent(content2)
            ) : (
              <Text className='text-base-black'>–</Text>
            )}
          </div>
        </div>
      </div>
    );
  });

  const DesktopContent = (
    <>
      <div />
      {getComparisonTableDropdownIndices('desktop').map((_, i) => (
        <div key={`${id}-empty-${i}`} />
      ))}

      {categoryTitles.map((titleItem, rowIdx) => {
        const applyBg = rowIdx % 2 === 1;
        const baseBg = applyBg ? 'bg-base-white' : 'bg-gray-50';

        return (
          <div key={`${id}-row-${rowIdx}`} className='group contents'>
            <div
              className={cn(
                baseBg,
                `
                  group-hover:bg-brand-100
                  text-body-m
                  flex h-full items-center rounded-l
                `
              )}
            >
              <ProductCategoryTitles {...titleItem} />
            </div>
            {getComparisonTableDropdownIndices('desktop').map(
              (_, columnIdx) => (
                <div
                  key={`${id}-cell-${rowIdx}-${columnIdx}`}
                  className={cn(
                    baseBg,
                    `group-hover:bg-brand-50
                    text-base-black
                    flex items-center rounded-r py-2 pl-12 pr-20 
                  `
                  )}
                >
                  {getCellContent(columnIdx, rowIdx) ?? <span />}
                </div>
              )
            )}
          </div>
        );
      })}
    </>
  );

  return {
    id,
    header: (
      <Text
        variant='subtitle-l-d'
        className='cursor-pointer'
        role='button'
        tabIndex={0}
        aria-expanded={false}
      >
        {title}
      </Text>
    ),
    children: (
      <>
        <div
          className={` hidden md:grid grid-cols-[160px_repeat(${maxColumns},_minmax(200px,_1fr))] gap-y-3`}
        >
          {DesktopContent}
        </div>
        <div className='flex flex-col gap-6 md:hidden'>{MobileContent}</div>
      </>
    ),
  };
};
